import { useCallback, useState, CSSProperties, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { hasValue, isFunction, redirectTo } from '@r-and-a-shared-ui/utils';
import { nanoid } from 'nanoid';

import styles from './DropdownButton.module.scss';

export type Option = {
  label: string;
  value: string;
  hidden?: boolean;
  href?: string;
};

export type DropdownButtonProps = {
  options: Option[];
  defaultValue?: Option;
  fontAwesomeIcon?: IconDefinition;
  SvgIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  backgroundColor?: string;
  textColor?: string;
  isLocked?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  onClick?: (...args: any[]) => void;
};

export const DropdownButton: React.FC<DropdownButtonProps> = ({
  options,
  defaultValue,
  isLocked,
  disabled,
  fontAwesomeIcon,
  SvgIcon,
  backgroundColor,
  textColor,
  isOpen: controlledOpen,
  onToggle,
  onClick,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selected, setSelected] = useState(defaultValue);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const open = controlledOpen ?? internalOpen;

  const handleToggle = useCallback(() => {
    if (isFunction(onToggle)) {
      onToggle();
    } else {
      setInternalOpen(!internalOpen);
    }
  }, [onToggle, setInternalOpen, internalOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        if (isFunction(onToggle)) {
          if (open) {
            onToggle();
          }
        } else {
          setInternalOpen(false);
        }
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onToggle]);

  const handleClick = useCallback(
    (option: Option) => {
      if (!isLocked) {
        setSelected(option);
      }

      handleToggle();

      if (option.href) {
        redirectTo(option.href, false, false, false);
      }

      if (isFunction(onClick)) {
        onClick(option);
      }
    },
    [isLocked, setSelected, handleToggle, onClick],
  );

  const renderOptions = () => {
    if (!hasValue(options)) {
      return null;
    }

    return options
      .filter(opt => !opt.hidden)
      .map((option: Option) => (
        <li
          key={`${option.label}-${nanoid(8)}`}
          className={styles['dropdown-item']}
          onClick={() => handleClick(option)}
        >
          {option.label}
        </li>
      ));
  };

  const inlineStyle: CSSProperties = {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(textColor ? { color: textColor } : {}),
  };

  return (
    <div className={`${styles['dropdown-wrapper']}`} ref={dropdownRef}>
      <button
        className={`${styles['dropdown-button']}`}
        onClick={handleToggle}
        disabled={disabled}
        style={inlineStyle}
      >
        <span className={styles['dropdown-icon']}>
          {fontAwesomeIcon ? (
            <FontAwesomeIcon icon={fontAwesomeIcon} width={22} height={22} />
          ) : (
            SvgIcon && <SvgIcon width={29} height={27} />
          )}
        </span>

        <span className={styles['dropdown-label']}>{selected?.label}</span>

        <span className={styles['dropdown-arrow-wrapper']}>
          {open ? (
            <FontAwesomeIcon icon={faAngleUp} />
          ) : (
            <FontAwesomeIcon icon={faAngleDown} />
          )}
        </span>
      </button>
      {open && (
        <ul className={`${styles['dropdown-menu']}`} style={inlineStyle}>
          {renderOptions()}
        </ul>
      )}
    </div>
  );
};

export default DropdownButton;
